import React, { useState, useEffect, useRef } from 'react';
import { Search, Folder, CheckCircle2, Loader2, Music, Play, Pause, Plus, Trash2, Rewind, FastForward } from 'lucide-react';
import services from '../../../API';
import ConfirmModal from '../../Modals/ConfirmModal';
import SceneAssetDropzone from './SceneAssetDropzone';

const AssetItem = ({ asset, isSelected, onSelect, togglePlay, playingTrackId, loadingTrackId, onDelete, onSkip, onClose }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const isImage = asset.type === 'image' || asset.mimeType?.startsWith('image/');
    const isVideo = asset.type === 'video' || asset.mimeType?.startsWith('video/');

    // For audio or other types, we consider them "loaded" immediately as they are static icons
    useEffect(() => {
        if (!isImage && !isVideo) {
            setIsLoaded(true);
        }
    }, [isImage, isVideo]);

    return (
        <div
            className="group relative flex flex-col cursor-pointer"
            onClick={() => {
                onSelect([asset]);
                if (onClose) onClose();
            }}
        >
            <div className={`relative aspect-[3/4] rounded-2xl overflow-hidden bg-white dark:bg-[#111111] ring-1 ring-black/5 dark:ring-white/10 shadow-sm group-hover:shadow-2xl group-hover:ring-orange-500/50 group-hover:scale-[1.02] transition-all duration-300 ${isSelected ? 'ring-orange-500 scale-95' : ''}`}>

                {/* Loading State */}
                {!isLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-white/5 z-10">
                        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                    </div>
                )}

                {isImage ? (
                    <img
                        src={asset.url}
                        crossOrigin="anonymous"
                        alt={asset.name}
                        className={`w-full h-full object-cover transition-transform group-hover:scale-110 ${!isLoaded ? 'opacity-0' : 'opacity-100'}`}
                        onLoad={() => setIsLoaded(true)}
                        onError={() => setIsLoaded(true)} // Handle error by showing placeholder or just revealing
                    />
                ) : isVideo ? (
                    <div className="relative w-full h-full">
                        <video
                            src={asset.url}
                            crossOrigin="anonymous"
                            className={`w-full h-full object-cover ${!isLoaded ? 'opacity-0' : 'opacity-100'}`}
                            onLoadedData={() => setIsLoaded(true)}
                            onError={() => setIsLoaded(true)}
                        />
                        <div className={`absolute inset-0 flex items-center justify-center bg-black/20 ${!isLoaded ? 'hidden' : ''}`}>
                            <Play className="w-8 h-8 text-white/50" />
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-full bg-gray-100 dark:bg-white/5 flex items-center justify-center relative">
                        <Music className="w-12 h-12 text-gray-300" />
                        <div className="absolute bottom-3 left-3 z-20 pointer-events-auto flex items-center gap-1">
                            {playingTrackId === asset._id && (
                                <button
                                    onClick={(e) => onSkip(e, -5)}
                                    className="w-6 h-6 rounded-full flex items-center justify-center bg-black/40 hover:bg-black/60 backdrop-blur-sm shadow-md border border-white/10 transition-colors"
                                    title="Skip backward 5s"
                                >
                                    <Rewind className="w-3 h-3 text-white fill-current" />
                                </button>
                            )}
                            <button
                                onClick={(e) => togglePlay(e, asset)}
                                className={`w-8 h-8 rounded-full flex items-center justify-center shadow-md transition-transform ${playingTrackId === asset._id ? 'bg-orange-600 scale-110' : 'bg-white/80 backdrop-blur-sm dark:bg-black/50 hover:bg-white dark:hover:bg-black hover:scale-110 border border-white/20'}`}
                            >
                                {loadingTrackId === asset._id ? (
                                    <Loader2 className={`w-4 h-4 animate-spin ${playingTrackId === asset._id ? 'text-white' : 'text-gray-700 dark:text-gray-200'}`} />
                                ) : playingTrackId === asset._id ? (
                                    <Pause className="w-4 h-4 text-white fill-white" />
                                ) : (
                                    <Play className="w-4 h-4 text-gray-700 dark:text-gray-200 fill-current ml-0.5" />
                                )}
                            </button>
                            {playingTrackId === asset._id && (
                                <button
                                    onClick={(e) => onSkip(e, 5)}
                                    className="w-6 h-6 rounded-full flex items-center justify-center bg-black/40 hover:bg-black/60 backdrop-blur-sm shadow-md border border-white/10 transition-colors"
                                    title="Skip forward 5s"
                                >
                                    <FastForward className="w-3 h-3 text-white fill-current" />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Overlays - only show when loaded */}
                {isLoaded && (
                    <>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                        {/* Add Icon on Hover */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all transform scale-90 group-hover:scale-100 duration-300 z-10 pointer-events-none">
                            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md border border-white/40 flex items-center justify-center shadow-2xl">
                                <Plus className="w-6 h-6 text-white" />
                            </div>
                        </div>

                        {/* Delete Icon */}
                        {onDelete && !isImage && !isVideo && (
                            <div className="absolute top-3 left-3 z-20 pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={(e) => { e.stopPropagation(); onDelete(asset); }}
                                    className="w-8 h-8 rounded-full bg-red-500/80 hover:bg-red-600 text-white flex items-center justify-center backdrop-blur-sm shadow-md transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {/* Selection Indicator */}
                        {isSelected && (
                            <div className="absolute top-3 right-3 z-20">
                                <CheckCircle2 className="w-6 h-6 text-orange-500 fill-white" />
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="mt-3">
                <div className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 tracking-wider mb-1">
                    {asset.type || 'ASSET'}
                </div>
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />}
                        <span
                            title={asset.name}
                            className={`text-xs font-semibold truncate cursor-pointer ${isSelected ? 'text-orange-500' : 'text-gray-700 dark:text-gray-200'}`}
                        >
                            {asset.name}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AdAssetLibraryModal = ({ isOpen, onClose, onSelect, allowedTypes = ['image', 'video', 'audio'], initialCategory = 'creativeAssets' }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchAllFolders, setSearchAllFolders] = useState(false);
    const [allAssets, setAllAssets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('creativeAssets');
    const canBrowseAudio = allowedTypes.includes('audio');
    const [playingTrackId, setPlayingTrackId] = useState(null);
    const [loadingTrackId, setLoadingTrackId] = useState(null);
    const audioRef = useRef(null);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [assetToDelete, setAssetToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        audioRef.current = new Audio();
        audioRef.current.onended = () => {
            setPlayingTrackId(null);
            setLoadingTrackId(null);
        };
        audioRef.current.onplaying = () => setLoadingTrackId(null);
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
        };
    }, []);

    const handleSkip = (e, seconds) => {
        e.stopPropagation();
        if (audioRef.current) {
            audioRef.current.currentTime = Math.max(0, Math.min(audioRef.current.currentTime + seconds, audioRef.current.duration || 0));
        }
    };

    useEffect(() => {
        if (!isOpen) {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
            setPlayingTrackId(null);
            setLoadingTrackId(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
        setPlayingTrackId(null);
        setLoadingTrackId(null);
    }, [selectedCategory]);

    useEffect(() => {
        if (!canBrowseAudio) {
            setSelectedCategory('creativeAssets');
        }
    }, [canBrowseAudio]);

    useEffect(() => {
        if (!isOpen) return;

        const canOpenAudioCategory = canBrowseAudio && ['music', 'soundEffect'].includes(initialCategory);
        setSelectedCategory(canOpenAudioCategory ? initialCategory : 'creativeAssets');
    }, [canBrowseAudio, initialCategory, isOpen]);

    const togglePlay = (e, track) => {
        e.stopPropagation();
        if (playingTrackId === track._id || loadingTrackId === track._id) {
            audioRef.current.pause();
            setPlayingTrackId(null);
            setLoadingTrackId(null);
        } else {
            if (audioRef.current) {
                setLoadingTrackId(track._id);
                setPlayingTrackId(track._id);
                audioRef.current.src = track.url;
                audioRef.current.play().catch(err => {
                    console.error("Playback failed:", err);
                    setPlayingTrackId(null);
                    setLoadingTrackId(null);
                });
            }
        }
    };

    const fetchAssets = async () => {
        setIsLoading(true);
        try {
            let combinedAssets = [];

            // 1. Standard assets
            try {
                const response = await services.getAssets();
                if (response?.data?.assets) {
                    combinedAssets = response.data.assets.filter(asset => asset.type !== 'other');
                }
            } catch (err) {
                console.error("Error fetching standard assets:", err);
            }

            // 2. Generated Music
            try {
                const musicRes = await services.getMusicAssets({ limit: 50, skip: 0, type: 'music' });
                if (musicRes?.data?.musics) {
                    const generatedMusic = musicRes.data.musics.map(m => ({
                        _id: m._id || m.id,
                        name: m.name || 'Generated Music',
                        url: m.musicUrl || m.url,
                        mimeType: 'audio/mpeg',
                        type: 'audio',
                        folderType: 'music',
                        duration: m.durationMs ? Math.round(m.durationMs / 1000) : 30
                    }));
                    combinedAssets = [...combinedAssets, ...generatedMusic];
                }
            } catch (err) {
                console.error("Error fetching music assets:", err);
            }

            // 3. Generated Sound Effects
            try {
                const sfxRes = await services.getMusicAssets({ limit: 50, skip: 0, type: 'soundEffect' });
                if (sfxRes?.data?.musics) {
                    const generatedSfx = sfxRes.data.musics.map(m => ({
                        _id: m._id || m.id,
                        name: m.name || 'Generated SFX',
                        url: m.musicUrl || m.url,
                        mimeType: 'audio/mpeg',
                        type: 'audio',
                        folderType: 'soundEffect',
                        duration: m.durationMs ? Math.round(m.durationMs / 1000) : 5
                    }));
                    combinedAssets = [...combinedAssets, ...generatedSfx];
                }
            } catch (err) {
                console.error("Error fetching sfx assets:", err);
            }

            setAllAssets(combinedAssets);
        } catch (error) {
            console.error("Error fetching assets:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchAssets();
        }
    }, [isOpen]);

    const handleSceneAssetsUploaded = async () => {
        await fetchAssets();
    };

    const handleDeleteClick = (asset) => {
        setAssetToDelete(asset);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!assetToDelete) return;
        setIsDeleting(true);
        try {
            if (assetToDelete.folderType) {
                await services.deleteMusicAssets([assetToDelete._id]);
            } else {
                await services.deleteMultipleAssets([assetToDelete._id]);
            }
            await fetchAssets();
            setIsDeleteModalOpen(false);
            setAssetToDelete(null);
        } catch (err) {
            console.error(err);
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen) return null;

    const isStandardAudio = (asset) =>
        asset.type === 'audio' ||
        asset.mimeType?.startsWith('audio/') ||
        ['mp3', 'wav', 'ogg', 'm4a'].some(ext => asset.name?.toLowerCase().endsWith(ext));

    const categories = [
        { id: 'creativeAssets', name: 'Creative Assets' },
        { id: 'music', name: 'Music' },
        { id: 'soundEffect', name: 'Sound Effects' },
    ];
    const selectedCategoryName = categories.find(category => category.id === selectedCategory)?.name || 'Creative Assets';

    // Filter assets within the selected Media Library category and search query.
    const filteredAssets = allAssets.filter(asset => {
        const isAudio = isStandardAudio(asset) || asset.folderType === 'music' || asset.folderType === 'soundEffect';
        const isImage = asset.type === 'image' || asset.mimeType?.startsWith('image/');
        const isVideo = asset.type === 'video' || asset.mimeType?.startsWith('video/');

        let categoryMatch = true;
        const isVisual = !asset.folderType && !isStandardAudio(asset);

        if (selectedCategory === 'music') {
            categoryMatch = asset.folderType === 'music' || (!asset.folderType && isStandardAudio(asset));
        } else if (selectedCategory === 'soundEffect') {
            categoryMatch = asset.folderType === 'soundEffect';
        } else {
            categoryMatch = isVisual;
        }

        const matchesSearch = asset.name?.toLowerCase().includes(searchQuery.toLowerCase());

        // Respect allowedTypes prop
        const isAllowed = allowedTypes.some(type => {
            if (type === 'image') return isImage;
            if (type === 'video') return isVideo;
            if (type === 'audio') return isAudio;
            return false;
        });

        return categoryMatch && matchesSearch && isAllowed;
    });

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="w-full max-w-7xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 bg-[#FAFAFA] dark:border-white/10 dark:bg-[#0A0A0A]">
                {/* Header */}
                <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 dark:border-white/5">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onClose}
                            className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors"
                        >
                            Close <span className="text-[10px] opacity-50 bg-gray-300 dark:bg-gray-600 px-1 rounded">Esc</span>
                        </button>
                        <div className="h-4 w-px bg-gray-200 dark:bg-white/10" />
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Media Library</h1>
                        <span className="px-3 py-1 bg-orange-100 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-bold uppercase rounded-full tracking-wider">
                            {allAssets.length} Items
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-300 transition-all active:scale-95"
                        >
                            Done
                        </button>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="px-8 py-6 flex items-center justify-between gap-6">
                    <div className="relative flex-1 max-w-2xl">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder={`Search ${selectedCategoryName.toLowerCase()}...`}
                            className="w-full bg-white dark:bg-[#111111] border border-gray-200 dark:border-white/5 rounded-2xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all text-gray-900 dark:text-white shadow-sm"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="hidden">
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Search all folders</span>
                            <button
                                onClick={() => setSearchAllFolders(!searchAllFolders)}
                                className={`w-10 h-5 rounded-full p-1 transition-colors relative ${searchAllFolders ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-700'}`}
                            >
                                <div className={`w-3 h-3 bg-white rounded-full transition-transform ${searchAllFolders ? 'translate-x-5' : 'translate-x-0'}`} />
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <button className="px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300">
                                Select all <span className="opacity-50 ml-1">⌘A</span>
                            </button>
                            <button className="px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300">
                                Show favorites <span className="opacity-50 ml-1">S</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Fast upload area: files are uploaded to the shared asset library immediately. */}
                <div className="px-8 pb-6">
                    <SceneAssetDropzone
                        onUploaded={handleSceneAssetsUploaded}
                        allowedTypes={allowedTypes}
                    />
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-8 pb-8">
                    {canBrowseAudio && (
                        <div className="mb-10">
                            <div className="inline-flex max-w-full overflow-x-auto rounded-xl border border-gray-200 bg-white p-1 shadow-sm dark:border-white/5 dark:bg-[#111111]">
                                {categories.map(category => {
                                    const isActive = selectedCategory === category.id;

                                    return (
                                        <button
                                            key={category.id}
                                            type="button"
                                            onClick={() => setSelectedCategory(category.id)}
                                            className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${isActive
                                                ? 'bg-orange-500 text-white shadow-sm'
                                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white'
                                                }`}
                                            aria-pressed={isActive}
                                        >
                                            {category.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Assets Grid */}
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-4">
                                <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">{selectedCategoryName}</h2>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-8">
                            {isLoading ? (
                                <div className="col-span-full flex flex-col items-center justify-center py-20 text-gray-400">
                                    <Loader2 className="w-10 h-10 animate-spin mb-4" />
                                    <p>Loading assets...</p>
                                </div>
                            ) : filteredAssets.length === 0 ? (
                                <div className="col-span-full flex flex-col items-center justify-center py-32 text-gray-500 bg-white dark:bg-white/5 rounded-3xl border border-dashed border-gray-200 dark:border-white/10">
                                    <div className="w-16 h-16 rounded-full bg-gray-50 dark:bg-white/5 flex items-center justify-center mb-4">
                                        <Folder className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                                    </div>
                                    <p className="text-lg font-medium mb-1">No {selectedCategory === 'music' ? 'music' : selectedCategory === 'soundEffect' ? 'sound effect' : 'creative'} assets found</p>
                                    <p className="text-sm opacity-60">Upload new assets to get started</p>
                                </div>
                            ) : (
                                filteredAssets.map(asset => {
                                    return (
                                        <AssetItem
                                            key={asset._id}
                                            asset={asset}
                                            isSelected={false}
                                            onSelect={onSelect}
                                            togglePlay={togglePlay}
                                            playingTrackId={playingTrackId}
                                            loadingTrackId={loadingTrackId}
                                            onDelete={handleDeleteClick}
                                            onSkip={handleSkip}
                                            onClose={onClose}
                                        />
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => { setIsDeleteModalOpen(false); setAssetToDelete(null); }}
                title="Delete Asset"
                message={`Are you sure you want to delete "${assetToDelete?.name || 'this asset'}"? This action cannot be undone.`}
                confirmLabel="Delete"
                cancelLabel="Cancel"
                onConfirm={handleConfirmDelete}
                zIndex={10000}
                isLoading={isDeleting}
            />
        </div>
    );
};

export default AdAssetLibraryModal;
